import { Auth0Client } from '@auth0/auth0-spa-js'
import { clearLocalStorage } from '../localstorage'
import { getDefaultAudience } from '@paintscout/util/src/users/getDefaultAudience/getDefaultAudience'
import { markForceLogin } from 'react-auth0'

export const audience = `https://api.${getDefaultAudience()}`

export const scope = `openid email profile https://${getDefaultAudience()}/user_metadata https://${getDefaultAudience()}/app_metadata offline_access`

export async function clearUserData(companyId: string) {
  clearLocalStorage({
    whitelist: [
      'calendarView',
      'calendarHideWeekends',
      'productionCalendarView',
      'productionCalendarHideWeekends',
      'productionCalendarUnscheduledCollapsed',
      'blockLaunchDialog',
      'dealTableColumns',
      'contactTableColumns',
      'companyTableColumns',
      // Only the company-scoped key. The legacy un-scoped `dashboardOptions` was whitelisted here
      // too, which kept a blob captured under one company alive forever — the next company logged
      // into on this browser adopted it (estimator ids included) and zeroed its dashboard (#22045).
      `dashboardOptions-${companyId}`,
      'quoteOptions.sidebarOpen',
      'sideNavExpanded',
      'quoteTableColumns',
      'taskTableColumns',
      'file-viewer-gallery-size' // GALLERY_SIZE_STORAGE_KEY — keep grey-bar placement sticky across logout (#21190)
    ],
    // `dealSort` and `dealFilters` are partial matches because the keys are scoped per client and
    // pipeline (`dealSort:${clientId}:${pipeline}`) — keeps the deal sorter (ENG-962) and the deal
    // search filters (ENG-941) sticky across logout.
    whiteListPartialMatch: ['reportState/', 'ps:devtools', 'chat-read-', 'dealSort', 'dealFilters']
  })
  markForceLogin()
}

export const auth = new Auth0Client({
  domain: process.env.AUTH0_DOMAIN as string,
  clientId: process.env.AUTH0_APP_CLIENT_ID as string,
  authorizationParams: {
    redirect_uri: `${process.env.PS_APP_URL}/login-callback`,
    audience,
    scope
  },
  // When the refresh-token grant is gone, `useRefreshTokensFallback: true` sent the SDK to a
  // hidden-iframe `/authorize?prompt=none` check. That check needs the Auth0 session cookie in a
  // third-party context, which Safari ITP and Chrome's third-party-cookie phase-out both block, so
  // it could not succeed — it only bought a wait of `authorizeTimeoutInSeconds` (60s) before
  // failing anyway. Since this call gates every GraphQL operation at context creation, that wait
  // was the ENG-1190 spinner. With the fallback off, a missing or dead grant rejects promptly with
  // `missing_refresh_token` / `invalid_grant` and `AuthorizeUser` sends the user to a real login.
  //
  // 10s (not 60s) bounds the remaining `/authorize` uses. `apps/admin` has run without the
  // fallback and on a 5s timeout for a long time; this brings the app into line.
  authorizeTimeoutInSeconds: 10,
  useRefreshTokens: true,
  cacheLocation: 'localstorage',
  useRefreshTokensFallback: false
})
